import { useNavigate } from 'react-router-dom';
import { PropertyGridSkeleton } from '../ui/Skeleton';
import { useLanguage } from '@/hooks/useLanguage';

interface Property {
  id: string;
  title: string;
  price: number;
  currency: string;
  type: string;
  operation: string;
  bedrooms?: number;
  bathrooms?: number;
  area: number;
  location: string;
  images: string[];
  features: string[];
  description: string;
  featured: boolean;
}

interface PropertyGridProps {
  properties: Property[];
  loading?: boolean;
  className?: string;
}

export function PropertyGrid({ properties, loading, className }: PropertyGridProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleViewDetails = (propertyId: string) => {
    navigate(`/propiedad/${propertyId}`);
    window.scrollTo(0, 0);
  };

  const handleContact = (propertyId: string) => {
    navigate(`/contacto?propiedad=${propertyId}`);
  };

  if (loading) {
    return <PropertyGridSkeleton count={6} className={className} />;
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg">{t('properties.noResultsMessage')}</div>
        <p className="text-gray-400 mt-2">
          {t('properties.tryAdjustingSearch')}
        </p>
      </div>
    );
  }

  const formatPrice = (price: number, currency: string) => {
    const symbol = currency === 'USD' ? '$' : '$';
    return `${symbol}${price.toLocaleString()}`;
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${className || ''}`}>
      {properties.map((property) => (
        <div key={property.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
          {/* Image */}
          <div
            className="relative h-48 overflow-hidden cursor-pointer"
            onClick={() => handleViewDetails(property.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleViewDetails(property.id);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`${t('property.viewDetailsOf')} ${property.title}`}
          >
            <img
              src={property.images[0]}
              alt={property.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
            
            {/* Badges */}
            <div className="absolute top-3 left-3 flex gap-2">
              <span className={`px-2 py-1 text-xs font-medium rounded ${
                property.operation === 'VENTA'
                  ? 'bg-blue-500 text-white'
                  : 'bg-green-500 text-white'
              }`}>
                {property.operation === 'VENTA' ? t('properties.sale_badge') : t('properties.rent_badge')}
              </span>
              {property.featured && (
                <span className="px-2 py-1 text-xs font-medium rounded bg-red-500 text-white">
                  {t('properties.featured_badge')}
                </span>
              )}
            </div>

            {/* Price */}
            <div className="absolute bottom-3 left-3">
              <div className="bg-black/70 text-white px-3 py-1 rounded-lg">
                <span className="text-lg font-bold">
                  {formatPrice(property.price, property.currency)}
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-2 line-clamp-2">
              <button
                className="text-left hover:text-blue-600 transition-colors w-full"
                onClick={() => handleViewDetails(property.id)}
                aria-label={`${t('property.viewDetailsOf')} ${property.title}`}
              >
                {property.title}
              </button>
            </h3>

            <div className="flex items-center text-gray-600 mb-3">
              <span className="text-sm">{property.location?.formattedAddress || t('properties.locationNotAvailable')}</span>
            </div>

            {/* Features */}
            <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
              {property.bedrooms && (
                <div className="flex items-center">
                  <span>🛏️ {property.bedrooms}</span>
                </div>
              )}
              {property.bathrooms && (
                <div className="flex items-center">
                  <span>🚿 {property.bathrooms}</span>
                </div>
              )}
              <div className="flex items-center">
                <span>📐 {property.area} m²</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => handleViewDetails(property.id)}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
              >
                {t('properties.viewDetails')}
              </button>
              <button
                onClick={() => handleContact(property.id)}
                className="border border-blue-600 text-blue-600 py-2 px-4 rounded hover:bg-blue-50 transition-colors"
              >
                {t('properties.contact')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}